import { modelManager } from '@/editor/manager/entityManager';
import { EFloorPlanEventName } from '@/editor/manager/entityManager/floorPlanManager';
import { globalStores } from '@/editor/stores';
import { EEntityType, EFaceSide, ESceneMode } from '@/editor/types';
import { EVisibleEntityType, TVisibleKey } from '@/editor/types/visible';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { debounce } from 'lodash-es';
import { Vector2 } from 'three';
import { pixelRatio3d } from '@/editor/math/const';
import { FLAGS } from '@/flags';

enum ESideStatus {
  NoRoom = 0,
  OnlyLeftRoom = 1,
  OnlyRightRoom = 2,
  BothRoom = 3
}

export class VisibleController {
  constructor() {
    editorEvent.on(EditorEventName.VIEW3D_CHANGE, this._debounceHideWall);
    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this._handleSceneModeChange);
    modelManager.floorPlanManager.addListener(
      EFloorPlanEventName.NOTIFY_EXIST_UPDATE,
      this._handleFloorPlanExistChange
    );
  }

  /**
   * 「导入 / 移除底图」四条路径唯一的汇聚点。
   * 开关归 store 所有，所以这条策略放在控制器而不是 manager 里
   */
  private _handleFloorPlanExistChange = () => {
    // 未开启默认隐藏时，增删都复位为可见（与已发布版本一致）
    if (!FLAGS.FEATURES.ENABLE_VISIBLE_V1) {
      globalStores.visibleOperation.setFloorPlanVisible(true);
      return;
    }

    // 底图移除后必须复位，否则残留的隐藏状态会让「显示全部」按钮一直可点
    if (!modelManager.floorPlanManager.existing) {
      globalStores.visibleOperation.setFloorPlanVisible(true);
      return;
    }

    // 新出现的底图 entity 默认可见，得把开关当前值施加上去。
    // 方案加载因此沿用默认隐藏，而用户导入的那条路径会在渲染完成后自己置为可见
    globalStores.visibleOperation.setFloorPlanVisible(globalStores.visibilityStore.visibleState.floorPlan !== false);
  };

  public setVisible(type: TVisibleKey, v: boolean) {
    switch (type) {
      case EVisibleEntityType.ColumnAndWall:
        globalStores.visibleOperation.setColumnAndWallEntitiesVisible(v);
        break;
      case EVisibleEntityType.RegionTextInfo:
        globalStores.visibleOperation.setRegionTextInfoVisible(v);
        break;
      case EVisibleEntityType.RoomTextInfo:
        globalStores.visibleOperation.setRoomTextInfoVisible(v);
        break;
      case EEntityType.FloorPlan:
        globalStores.visibleOperation.setFloorPlanVisible(v);
        break;
      case EVisibleEntityType.Ruler:
        globalStores.visibleOperation.setRulerVisible(v);
        break;
      case EEntityType.Furniture:
        globalStores.visibleOperation.setFurnitureVisible(v);
        break;
      case EEntityType.AreaDimension:
        globalStores.visibleOperation.setAreaDimensionVisible(v);
        break;
      default:
        // 走到这里只剩隔墙和门窗，它们的开关 key 就是实体类型本身
        globalStores.visibleOperation.setSingleEntityTypeVisible(type, v);
        break;
    }
  }

  public showAll() {
    globalStores.visibleOperation.showAll();
  }

  // ============================= 3d 视角变化，墙体显隐 =============================

  private _hideWall = () => {
    if (globalStores.sceneMode !== ESceneMode.scene3d) return;

    // 获取所有房间的轮廓线
    const wallIdMap = new Map<string, number>(); // wallId -> 0/1/2/3 （0表示没房间关联，1表示wall左侧有房间，2表示wall右侧有房间，3表示两侧有房间）
    globalStores.separateWallStore.baseSeparateWallDatas.forEach((sepData) => {
      let status = wallIdMap.get(sepData.relativeWallId) || ESideStatus.NoRoom;
      if (sepData.side === EFaceSide.MainLeftSide) {
        if (status === ESideStatus.NoRoom) {
          status = ESideStatus.OnlyLeftRoom;
        } else if (status === ESideStatus.OnlyRightRoom) {
          status = ESideStatus.BothRoom;
        }
      } else if (sepData.side === EFaceSide.MainRightSide) {
        if (status === ESideStatus.NoRoom) {
          status = ESideStatus.OnlyRightRoom;
        } else if (status === ESideStatus.OnlyLeftRoom) {
          status = ESideStatus.BothRoom;
        }
      }
      wallIdMap.set(sepData.relativeWallId, status);
    });
    const _v1 = new Vector2();
    const _v2 = new Vector2();
    const _v3 = new Vector2();
    const _v4 = new Vector2();
    const cameraPosition = globalStores.editorStateStore.sceneInfo.scene3d.camera.position;
    const cameraPositionV2 = new Vector2(cameraPosition.x * pixelRatio3d, cameraPosition.y * pixelRatio3d);
    const hideWallIds = Array.from(wallIdMap.keys()).filter((wallId) => {
      const status = wallIdMap.get(wallId);
      if (status === ESideStatus.NoRoom || status === ESideStatus.BothRoom) return false;

      // 计算法线
      const wallData = globalStores.wallStore.getWallData(wallId);
      if (!wallData) return false;

      _v1.set(wallData.baseData.start.x, wallData.baseData.start.y);
      _v2.set(wallData.baseData.end.x, wallData.baseData.end.y);
      _v3.subVectors(_v2, _v1);
      _v4.addVectors(_v1, _v2); // 中心点
      _v4.divideScalar(2);
      _v1.subVectors(cameraPositionV2, _v4); // 中心点到相机的向量
      _v2.set(0, 0); // 旋转中心点
      if (status === ESideStatus.OnlyLeftRoom) {
        _v3.rotateAround(_v2, -Math.PI / 2);
      } else if (status === ESideStatus.OnlyRightRoom) {
        _v3.rotateAround(_v2, Math.PI / 2);
      }

      return _v1.dot(_v3) > 0; // 相机向量和法线的点积
    });

    globalStores.visibilityStore.set3dViewChangeHiddenWallIds(new Set(hideWallIds));
    modelManager.faceManager.update();
    modelManager.windowManager.update();

    // TODO: hover / selected 状态检查
  };

  private _debounceHideWall = debounce(this._hideWall, 50);

  private _handleSceneModeChange = (mode: ESceneMode) => {
    switch (mode) {
      case ESceneMode.scene2d: {
        // 切换至 2D 时需要恢复隐藏的墙体，避免墙上的构件显示异常
        this._reset3dHideWall();
        break;
      }
      case ESceneMode.scene3d: {
        // 切换到 3D 时需要执行一次隐藏
        this._hideWall();
        break;
      }
    }
  };

  /** 恢复隐藏的墙体 */
  private _reset3dHideWall() {
    globalStores.visibilityStore.set3dViewChangeHiddenWallIds(new Set());
    modelManager.faceManager.update();
    modelManager.windowManager.update();
  }

  // ============================= 其他 =============================
  dispose() {
    editorEvent.off(EditorEventName.VIEW3D_CHANGE, this._debounceHideWall);
    editorEvent.off(EditorEventName.SCENE_MODE_CHANGE, this._handleSceneModeChange);
  }
}
